import { ButtonGroup, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, ToggleButton, Box, IconButton, Button, Chip } from "@mui/material";
import { BottomAppBar } from "../../app/BottomAppBar";
import { addSplit, AppBlock, AppRow, deleteBlock, removeSplit, selectImportedUrs, Split, undeleteBlock } from "../ursOffer/importedUrsSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { importUrsData, imporUrsHeader, IUrsBody, ISection, IReq } from "../documents/import/ursImport";
import { nanoid } from "@reduxjs/toolkit";
import { showError } from "../notifications/notificationsSlice";
import { LoadingButton } from "@mui/lab";
import { Urs } from "./UrsSlice";
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import DeleteIcon from '@mui/icons-material/Delete';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { If } from "../../app/If";
import { store } from "../../app/store";
import { distance } from "../../app/levenshtein";
import { impossible } from "../../app/Utils";

export type UrsMetadata =
    { version: '1', layout: 'flat' | 'structure' }

export type BlockMeta
    = { type: 'h', content: string }
    | { type: 'p', content: string }
    | { type: 'th', tableId: string, cols: string[], split: Split | undefined }
    | { type: 'tr', tableId: string, cols: string[], split: Split | undefined }
    | { type: 'ul' }
    | { type: 'ol' }

export type ReqMeta
    = { version: '1', blockMeta: BlockMeta }

function onList(arr: number[], x): boolean {
    return arr.findIndex(y => x === y) >= 0
}

export const ImportedSupplierUrsPreview = (props: { onSuccess: (urs: Urs) => Promise<void> }) => {
    const dispatch = useAppDispatch()
    const { data, deleted, splits } = useAppSelector(selectImportedUrs)
    const navigate = useNavigate()

    const [saving, setSaving] = useState(false)

    const handleDiscardClick = useCallback(() => navigate(-1), [])

    const handleFinalizeImportClick = async () => {
        if (data === null) return
        const { title, structureId, ursNumber, relatedCompanyName, relatedDocumentId, blocks: allBlocks } = data
        setSaving(true)
        // const blocks = allBlocks.filter((_, idx) => !onList(deleted, idx))
        const blocks = allBlocks
        try {
            const newUrs = await dispatch(imporUrsHeader({
                description: title,
                structureId,
                title,
                ursNumber,
                language: 'EN',
                relatedCompanyName,
                relatedDocumentId
            })).unwrap()

            let requirements: IReq[] = []
            for (let i = 0; i < blocks.length; i++) {
                const block = blocks[i]

                if (onList(deleted, i)) continue

                switch (block.type) {
                    case 'h':
                        const hMeta: BlockMeta = { type: 'h', content: block.content }
                        requirements.push({
                            code: '',
                            name: block.content,
                            criticality: 'E',
                            jsonContent: JSON.stringify(hMeta),
                            searcheableContent: null,
                            childRequirements: [],
                        })
                        break
                    case 'p':
                        const pMeta: BlockMeta = { type: 'p', content: block.content }
                        requirements.push({
                            code: '',
                            name: block.content,
                            criticality: 'E',
                            jsonContent: JSON.stringify(pMeta),
                            searcheableContent: null,
                            childRequirements: [],
                        })
                        break
                    case 'table':
                        const tableId = nanoid()
                        for (let j = 0; j < block.rows.length; j++) {
                            const row = block.rows[j];

                            const split = splits.find(s => s.index === i && s.rowIndex === j)

                            const tableMeta: BlockMeta = {
                                type: row.type,
                                tableId,
                                cols: row.cols,
                                split,
                            }
                            
                            let childRequirements: IReq[] = []
                            if (split) {
                                childRequirements = split.content.map(c => {
                                    return {
                                        code: '',
                                        name: c,
                                        criticality: 'E',
                                        jsonContent: JSON.stringify({ type: 'p', content: c }),
                                        searcheableContent: null,
                                        childRequirements: [],
                                    }
                                })
                            }

                            requirements.push({
                                code: '',
                                name: row.plain,
                                criticality: 'E',
                                jsonContent: JSON.stringify(tableMeta),
                                searcheableContent: null,
                                childRequirements: childRequirements,
                            })
                        }
                        break
                    case 'ol':
                        const olMeta: BlockMeta = {
                            type: 'ol'
                        }
                        requirements.push({
                            code: '',
                            name: 'list',
                            criticality: 'E',
                            jsonContent: JSON.stringify(olMeta),
                            searcheableContent: null,
                            childRequirements: block.items.map(b => {
                                return {
                                    code: '',
                                    name: b.content,
                                    criticality: 'E',
                                    jsonContent: JSON.stringify({ type: 'li' }),
                                    searcheableContent: null,
                                    childRequirements: [],
                                }
                            }),
                        })
                        break
                    case 'ul':
                        const ulMeta: BlockMeta = {
                            type: 'ul'
                        }
                        requirements.push({
                            code: '',
                            name: 'list',
                            criticality: 'E',
                            jsonContent: JSON.stringify(ulMeta),
                            searcheableContent: null,
                            childRequirements: block.items.map(b => {
                                return {
                                    code: '',
                                    name: b.content,
                                    criticality: 'E',
                                    jsonContent: JSON.stringify({ type: 'li' }),
                                    searcheableContent: null,
                                    childRequirements: [],
                                }
                            }),
                        })
                        break
                }
            }

            const syntheticSection: ISection = {
                name: "Synthetic",
                requirements,
                groups: [],
            }
            const meta: UrsMetadata = { version: '1', layout: 'flat' }
            const payload: IUrsBody = {
                types: [syntheticSection],
                metadata: JSON.stringify(meta)
            }

            const result = await dispatch(importUrsData({
                id: newUrs.id,
                body: payload,
            })).unwrap()

            await props.onSuccess(result)

            navigate(`/supplier-imported-urs/edit/${newUrs.id}`, { replace: true })

        } catch (error) {
            dispatch(showError("error"))
        } finally {
            setSaving(false)
        }

    }

    if (data === null) return <div>No data</div>

    return <>
        <Container sx={{
            border: '1px dashed var(--gray-400)',
            height: 'calc(100vh - 150px)',
            overflow: 'auto',
        }} fixed maxWidth='lg'>{data.blocks.map((block, idx) => <RenderBlock key={idx} block={block} index={idx} />)}</Container>
        <BottomAppBar>
            <ButtonGroup>
                <LoadingButton loading={saving} onClick={handleDiscardClick} color="error" variant="outlined">Discard & go back</LoadingButton>
                <LoadingButton  loading={saving} variant="contained" onClick={handleFinalizeImportClick}>Finish import</LoadingButton>
            </ButtonGroup>
        </BottomAppBar>
    </>
}

const RenderBlock = (props: { block : AppBlock, index: number }) => {
    const { block, index } = props
    switch (block.type) {
        case 'h':
            return <AppHeader block={block} index={index} />
        case 'p':
            return <AppParagraph block={block} index={index} />
        case 'table':
            return <AppTable colsCount={block.colsCount} rows={block.rows} index={index} />
        case 'ul':
            return <AppUnorderedList block={block} index={index} />
        case 'ol':
            return <AppOrderedList block={block} index={index} />
    }
}

const AppHeader = (props: { block: AppBlock, index: number }) => {
    const { block, index } = props
    const dispatch = useAppDispatch()
    const [deleted, setDeleted] = useState(false)

    if (block.type !== 'h') return null

    const handleToggleDelete = () => {
        if (deleted) {
            setDeleted(false)
            dispatch(undeleteBlock(index))
        } else {
            setDeleted(true)
            dispatch(deleteBlock(index))
        }
    }

    return <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography 
            variant="h6" 
            sx={{
                flex: 1,
                [`&.block-deleted`]: {
                    bgcolor: 'var(--red-200)',
                    textDecorationLine: 'line-through',
                },
            }}
            className={deleted ? 'block-deleted' : ''}
        >
            <div dangerouslySetInnerHTML={{ __html: block.content }}></div>
        </Typography>
        <ToggleButton
            value="delete"
            selected={deleted}
            onChange={handleToggleDelete}
            size="small"
            color="error"
            sx={{ minWidth: '40px', height: '32px', mr: 2 }}
        >
            {deleted ? <RestoreFromTrashIcon /> : <DeleteIcon />}
        </ToggleButton>
    </div>
}

const AppParagraph = (props: { block: AppBlock, index: number }) => {
    const { block, index } = props
    const dispatch = useAppDispatch()
    const [deleted, setDeleted] = useState(false)

    if (block.type !== 'p') return null

    const handleToggleDelete = () => {
        if (deleted) {
            setDeleted(false)
            dispatch(undeleteBlock(index))
        } else {
            setDeleted(true)
            dispatch(deleteBlock(index))
        }
    }

    return <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Typography 
            variant="body1" 
            sx={{
                flex: 1,
                [`&.block-deleted`]: {
                    bgcolor: 'var(--red-200)',
                    textDecorationLine: 'line-through',
                },
            }}
            className={deleted ? 'block-deleted' : ''}
        >
            <div dangerouslySetInnerHTML={{ __html: block.content }}></div>
        </Typography>
        <ToggleButton
            value="delete"
            selected={deleted}
            onChange={handleToggleDelete}
            size="small"
            color="error"
            sx={{ minWidth: '40px', height: '32px', mr: 2 }}
        >
            {deleted ? <RestoreFromTrashIcon /> : <DeleteIcon />}
        </ToggleButton>
    </div>
}

const AppOrderedList = (props: { block: AppBlock, index: number }) => {
    const { block, index } = props
    const dispatch = useAppDispatch()
    const [deleted, setDeleted] = useState(false)

    if (block.type !== 'ol') return null

    const handleToggleDelete = () => {
        if (deleted) {
            setDeleted(false)
            dispatch(undeleteBlock(index))
        } else {
            setDeleted(true)
            dispatch(deleteBlock(index))
        }
    }

    return <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <ol 
            style={{ 
                flex: 1, 
                margin: 0,
                backgroundColor: deleted ? 'var(--red-200)' : 'transparent',
                textDecorationLine: deleted ? 'line-through' : 'none',
            }}
        >
            {block.items.map((i, idx) => <li key={idx} dangerouslySetInnerHTML={{ __html: i.content }}></li>)}
        </ol>
        <ToggleButton
            value="delete"
            selected={deleted}
            onChange={handleToggleDelete}
            size="small"
            color="error"
            sx={{ minWidth: '40px', height: '32px', mr: 2 }}
        >
            {deleted ? <RestoreFromTrashIcon /> : <DeleteIcon />}
        </ToggleButton>
    </div>
}

const AppUnorderedList = (props: { block: AppBlock, index: number }) => {
    const { block, index } = props
    const dispatch = useAppDispatch()
    const [deleted, setDeleted] = useState(false)

    if (block.type !== 'ul') return null

    const handleToggleDelete = () => {
        if (deleted) {
            setDeleted(false)
            dispatch(undeleteBlock(index))
        } else {
            setDeleted(true)
            dispatch(deleteBlock(index))
        }
    }

    return <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        <ul 
            style={{ 
                flex: 1, 
                margin: 0,
                backgroundColor: deleted ? 'var(--red-200)' : 'transparent',
                textDecorationLine: deleted ? 'line-through' : 'none',
            }}
        >
            {block.items.map((i, idx) => <li key={idx} dangerouslySetInnerHTML={{ __html: i.content }}></li>)}
        </ul>
        <ToggleButton
            value="delete"
            selected={deleted}
            onChange={handleToggleDelete}
            size="small"
            color="error"
            sx={{ minWidth: '40px', height: '32px', mr: 2 }}
        >
            {deleted ? <RestoreFromTrashIcon /> : <DeleteIcon />}
        </ToggleButton>
    </div>
}

function AppTable(props: { colsCount: number, rows: AppRow[], index: number }) {
    const dispatch = useAppDispatch()

    const { colsCount, rows, index: blockIndex } = props

    const deleted = useAppSelector(state => selectImportedUrs(state).deleted.findIndex(x => x === blockIndex) >= 0)

    const [splitStates, setSplitStates] = useState<Record<string, boolean>>({})
    const [found, setFound] = useState<number | null>(null)

    const handleToggleDelete = () => {
        if (deleted) {
            dispatch(undeleteBlock(props.index))
        } else {
            dispatch(deleteBlock(props.index))
        }
    }

    const handleFindSimilarClick = () => {
        const allBlocks = selectImportedUrs(store.getState()).data?.blocks ?? []

        const current = allBlocks[blockIndex] ?? impossible()
        const currentTxt = current?.innerText ?? ''

        let findings: number[] = []

        const threshold: number = Math.ceil(currentTxt.length * .2)

        for (let i = 0; i < allBlocks.length; i++) {
            const block = allBlocks[i];

            if (block.type !== 'table' || i === blockIndex) continue;

            const candidateTxt = block.innerText ?? ''

            // console.log('Checking: ', candidateTxt)

            const d = distance(currentTxt, candidateTxt)

            // console.log('Distance = ', d)

            let lengthRatio = 0
            if (currentTxt.length > 0 && candidateTxt.length > 0) {
                lengthRatio = currentTxt.length / candidateTxt.length
            }

            // console.log('Current length', currentTxt.length)
            // console.log('Candidate length', candidateTxt.length)
            // console.log('Length ratio: ', lengthRatio)
            // console.log('Threshold: ', threshold)

            if (d < threshold && lengthRatio > .9 && lengthRatio < 1.1) {
                findings.push(i)
            }
        }

        for (let j = 0; j < findings.length; j++) {
            const item = findings[j];
            dispatch(deleteBlock(item))
        }
        setFound(findings.length)
    }

    const handleToggleSplit = (cellKey: string) => {
        setSplitStates(prev => ({
            ...prev,
            [cellKey]: !prev[cellKey]
        }))
    }

    const firstTrIdx = rows.findIndex(r => r.type === 'tr')
    const ths = rows.slice(0, firstTrIdx)
    const trs = rows.slice(firstTrIdx)

    const actions = <Box sx={{
        display: 'flex',
        gap: 1,
    }}>
        <ToggleButton
            value="delete"
            selected={deleted}
            onChange={handleToggleDelete}
            size="small"
            color="error"
            sx={{ minWidth: '40px', height: '32px' }}
        >
            {deleted ? <RestoreFromTrashIcon /> : <DeleteIcon />}
        </ToggleButton>
    </Box>
 
    return <TableContainer sx={{
        my: 1,
        [`& .block-deleted`]: {
            bgcolor: 'var(--red-200)',
            textDecorationLine: 'line-through',
        },
    }} component={Paper}>
        <Table className={deleted ? 'block-deleted': ''}>
            {ths.length > 0 && <TableHead>
                {ths.map((row, rowIdx) => <TableRow key={rowIdx}>
                    {row.cols.map((col, colIdx) => <TableCell key={colIdx} sx={{ minWidth: '150px' }}><span dangerouslySetInnerHTML={{ __html: col}}></span></TableCell>)}
                    <TableCell sx={{ width: '80px', textAlign: 'center' }}>{actions}</TableCell>
                </TableRow>)}
            </TableHead>}
            <TableBody>
                {trs.map((row, rowIdx) => <TableRow key={rowIdx}>
                    {row.cols.map((col, colIdx) => {
                        return <AppTableCell key={colIdx} colIndex={colIdx} content={col} rowIndex={rowIdx + ths.length} blockIndex={blockIndex} />
                    })}
                    <TableCell sx={{ textAlign: 'center' }}>
                        <If condition={ths.length === 0}>
                            {actions}
                        </If>
                    </TableCell>
                </TableRow>)}
            </TableBody>
        </Table>
        <If condition={deleted}>
            <Box sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'center',
                bgcolor: 'var(--red-200)',
                p: .5,
            }}>
                <Button variant='contained' size='small' startIcon={<DeleteSweepIcon />} onClick={handleFindSimilarClick}>Find similar</Button>
                {found !== null && <Chip size='small' variant="outlined" color='error' label={`${found} items found`} />}
            </Box>
        </If>
    </TableContainer>
}

const AppTableCell = (props: { content: string, rowIndex: number, colIndex: number, blockIndex: number }) => {
    const { content, rowIndex, blockIndex, colIndex } = props
    const dispatch = useAppDispatch()
    const hasBr = containsBrTag(content)
    const [isSplit, setIsSplit] = useState(false)
    // const splitContent = isSplit ? splitRequirementContent(content) : []
    const splitted = useMemo(() => splitRequirementContent(content), [])

    const handleToggleSplit = () => {
        setIsSplit(!isSplit)
        if (!isSplit) {
            dispatch(addSplit({ index: blockIndex, rowIndex, content: splitted, colIndex }))
        } else {
            dispatch(removeSplit({ index: blockIndex, rowIndex, content: splitted, colIndex }))
        }
    }
    
    return <TableCell>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ flex: 1 }}>
                {isSplit ? (
                    <div>
                        {splitted.map((item, itemIdx) => (
                            <div 
                                key={itemIdx}
                                style={{
                                    border: '1px dashed var(--gray-400)',
                                    padding: '8px',
                                    margin: '4px 0',
                                    borderRadius: '4px'
                                }}
                                dangerouslySetInnerHTML={{ __html: item }}
                            />
                        ))}
                    </div>
                ) : (
                    <p dangerouslySetInnerHTML={{ __html: content }}></p>
                )}
            </div>
            {hasBr && (
                <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <ToggleButton
                        value="split"
                        selected={isSplit}
                        onChange={() => handleToggleSplit()}
                        size="small"
                        color="primary"
                        sx={{ minWidth: '40px', height: '32px' }}
                    >
                        {isSplit ? <><CallMergeIcon /> Merge</> : <><CallSplitIcon /> Split</>}
                    </ToggleButton>
                </div>
            )}
        </div>
    </TableCell>
}

function splitRequirementContent(content: string): string[] {
    const lines = content.split('<br>')
    let result: string[] = [lines[0]]

    for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (line.length === 0) continue
        
        const firstChar = line[0]
        if (firstChar.match(/[A-Z]/) || !firstChar.match(/[a-z]/)) {
            result.push(lines[i])
        } else {
            result[result.length - 1] += lines[i]
        }
    }

    return result
}

function containsBrTag(content: string): boolean {
    return content.includes('<br>')
}
